/**
 * PolicyConfigModal Component
 *
 * Unified modal dialog for creating Conditional Access policies.
 * Integrates policy type dropdown directly in the configuration form.
 */

import { useState, useEffect } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  Switch,
  Dropdown,
  Option,
  Text,
  Checkbox,
  Radio,
  RadioGroup,
  Divider,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { AsyncButton } from '../../common/AsyncButton';
import { ConditionalAccessApiService } from '../../../services/conditionalAccessApi';
import { AppProtectionApiService } from '../../../services/appProtectionApi';
import { Alert } from '../../common/Alert';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '70vw',
    height: '80vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(120px)', // Center on content area (240px sidebar ÷ 2)
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minHeight: '0',
  },
  scrollableContent: {
    overflowY: 'auto',
    flex: '1',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalL, '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      ...shorthands.borderBottom('none'),
    },
  },
  formRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    alignItems: 'flex-end',
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  policyStateDropdown: {
    maxWidth: '200px',
  },
  persistentBrowserDropdown: {
    maxWidth: '150px',
  },
  caeDropdown: {
    maxWidth: '250px',
  },
  policyTypeDropdown: {
    maxWidth: '400px',
  },
  excludedUsersInput: {
    maxWidth: '600px',
  },
  actions: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    justifyContent: 'flex-end',
  },
});

type PolicyType = 'session-controls' | 'block-legacy-auth' | 'passwordless-enforcement' | 'risk-signin' | 'risk-user' | 'app-protection' | null;

interface InitialPolicy {
  id: string;
  displayName: string;
  policy_type: string;
  sign_in_frequency_hours?: number;
  sign_in_frequency_days?: number;
  persistent_browser_mode?: 'always' | 'never';
  cae_mode?: 'strictEnforcement' | 'disabled' | 'strictLocation';
  state?: 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced';
  exclude_users?: string[];
  risk_levels?: string[];
  risk_action?: 'block' | 'mfa' | 'passwordChange';
}

interface PolicyConfigModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  initialPolicy?: InitialPolicy;
  onClose: () => void;
  tenantId: string;
  onSuccess: () => void;
}

export const PolicyConfigModal: React.FC<PolicyConfigModalProps> = ({
  isOpen,
  mode = 'create',
  initialPolicy,
  onClose,
  tenantId,
  onSuccess,
}) => {
  const styles = useStyles();

  const [policyType, setPolicyType] = useState<PolicyType>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Common policy configuration
  const [excludedUsers, setExcludedUsers] = useState<string>('');
  const [policyState, setPolicyState] = useState<'enabled' | 'disabled' | 'enabledForReportingButNotEnforced'>('enabledForReportingButNotEnforced');

  // Session Controls form state
  const [signInFrequencyEnabled, setSignInFrequencyEnabled] = useState<boolean>(false);
  const [signInFrequencyValue, setSignInFrequencyValue] = useState<number>(1);
  const [signInFrequencyType, setSignInFrequencyType] = useState<'hours' | 'days'>('hours');

  const [persistentBrowserEnabled, setPersistentBrowserEnabled] = useState<boolean>(false);
  const [persistentBrowserMode, setPersistentBrowserMode] = useState<'always' | 'never'>('never');

  const [caeMode, setCaeMode] = useState<'strictEnforcement' | 'disabled' | 'strictLocation'>('disabled');

  // Risk-based policy form state
  const [riskLevels, setRiskLevels] = useState<string[]>(['high']);
  const [riskAction, setRiskAction] = useState<'block' | 'mfa' | 'passwordChange'>('block');

  // App Protection (MAM) policy form state
  const [mamPlatform, setMamPlatform] = useState<'android' | 'ios'>('android');
  const [mamDisplayName, setMamDisplayName] = useState<string>('');
  const [mamDescription, setMamDescription] = useState<string>('');
  const [mamPinRequired, setMamPinRequired] = useState<boolean>(false);
  const [mamMinimumPinLength, setMamMinimumPinLength] = useState<number>(4);
  const [mamFaceIdBlocked, setMamFaceIdBlocked] = useState<boolean>(false);

  const [validationError, setValidationError] = useState<string | null>(null);

  // Reset form when modal opens (create mode) or populate from initialPolicy (edit mode)
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialPolicy) {
        // Edit mode: populate from initialPolicy
        setPolicyType(initialPolicy.policy_type as PolicyType);
        setExcludedUsers(initialPolicy.exclude_users?.join(', ') || '');
        setPolicyState(initialPolicy.state || 'enabledForReportingButNotEnforced');

        // Session Controls fields
        if (initialPolicy.sign_in_frequency_hours || initialPolicy.sign_in_frequency_days) {
          setSignInFrequencyEnabled(true);
          if (initialPolicy.sign_in_frequency_hours) {
            setSignInFrequencyValue(initialPolicy.sign_in_frequency_hours);
            setSignInFrequencyType('hours');
          } else if (initialPolicy.sign_in_frequency_days) {
            setSignInFrequencyValue(initialPolicy.sign_in_frequency_days);
            setSignInFrequencyType('days');
          }
        } else {
          setSignInFrequencyEnabled(false);
          setSignInFrequencyValue(1);
          setSignInFrequencyType('hours');
        }

        if (initialPolicy.persistent_browser_mode) {
          setPersistentBrowserEnabled(true);
          setPersistentBrowserMode(initialPolicy.persistent_browser_mode);
        } else {
          setPersistentBrowserEnabled(false);
          setPersistentBrowserMode('never');
        }

        setCaeMode(initialPolicy.cae_mode || 'disabled');

        // Risk-based policy fields
        setRiskLevels(initialPolicy.risk_levels || ['high']);
        setRiskAction(initialPolicy.risk_action || 'block');
      } else {
        // Create mode: reset to defaults
        setPolicyType(null);
        setExcludedUsers('');
        setPolicyState('enabledForReportingButNotEnforced');
        setSignInFrequencyEnabled(false);
        setSignInFrequencyValue(1);
        setSignInFrequencyType('hours');
        setPersistentBrowserEnabled(false);
        setPersistentBrowserMode('never');
        setCaeMode('disabled');
        setRiskLevels(['high']);
        setRiskAction('block');
        setMamPlatform('android');
        setMamDisplayName('');
        setMamDescription('');
        setMamPinRequired(false);
        setMamMinimumPinLength(4);
        setMamFaceIdBlocked(false);
      }
      setError(null);
      setValidationError(null);
    }
  }, [isOpen, mode, initialPolicy]);

  // Validate sign-in frequency value
  useEffect(() => {
    if (policyType === 'session-controls' && signInFrequencyEnabled) {
      if (signInFrequencyType === 'hours' && (signInFrequencyValue < 1 || signInFrequencyValue > 24)) {
        setValidationError('Hours must be between 1 and 24');
      } else if (signInFrequencyType === 'days' && (signInFrequencyValue < 1 || signInFrequencyValue > 365)) {
        setValidationError('Days must be between 1 and 365');
      } else {
        setValidationError(null);
      }
    } else {
      setValidationError(null);
    }
  }, [policyType, signInFrequencyEnabled, signInFrequencyValue, signInFrequencyType]);

  const handleClear = () => {
    setPolicyType(null);
    setExcludedUsers('');
    setPolicyState('enabledForReportingButNotEnforced');
    setSignInFrequencyEnabled(false);
    setSignInFrequencyValue(1);
    setSignInFrequencyType('hours');
    setPersistentBrowserEnabled(false);
    setPersistentBrowserMode('never');
    setCaeMode('disabled');
    setRiskLevels(['high']);
    setRiskAction('block');
    setError(null);
    setValidationError(null);
  };

  const handleSave = async () => {
    if (!policyType || validationError) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Parse excluded users (comma-separated)
      const excludedUsersList = excludedUsers
        .split(',')
        .map((u) => u.trim())
        .filter((u) => u.length > 0);

      if (policyType === 'session-controls') {
        // Build request for unified CA API
        const request = {
          policy_name: 'Session Controls',
          policy_type: 'session-controls' as const,
          sign_in_frequency_hours: signInFrequencyEnabled && signInFrequencyType === 'hours' ? signInFrequencyValue : undefined,
          sign_in_frequency_days: signInFrequencyEnabled && signInFrequencyType === 'days' ? signInFrequencyValue : undefined,
          persistent_browser_mode: persistentBrowserEnabled ? persistentBrowserMode : undefined,
          cae_mode: caeMode,
          target_users: null,
          exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
          state: policyState,
        };

        if (mode === 'edit' && initialPolicy?.id) {
          // Edit mode: use UPDATE API
          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          // Create mode: use CREATE API
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request);
        }
      } else if (policyType === 'block-legacy-auth') {
        const request = {
          policy_name: 'Block Legacy Authentication',
          policy_type: 'block-legacy-auth' as const,
          target_users: null,
          exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
          state: policyState,
        };

        if (mode === 'edit' && initialPolicy?.id) {
          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request);
        }
      } else if (policyType === 'passwordless-enforcement') {
        const request = {
          policy_name: 'Passwordless Enforcement',
          policy_type: 'passwordless-enforcement' as const,
          target_users: null,
          exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
          state: policyState,
        };

        if (mode === 'edit' && initialPolicy?.id) {
          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request);
        }
      } else if (policyType === 'risk-signin') {
        const request = {
          policy_name: 'Block High Risk Sign-Ins',
          policy_type: 'risk-signin' as const,
          risk_levels: riskLevels,
          target_users: null,
          exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
          state: policyState,
        };

        if (mode === 'edit' && initialPolicy?.id) {
          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request);
        }
      } else if (policyType === 'risk-user') {
        const request = {
          policy_name: 'Require MFA for High Risk Users',
          policy_type: 'risk-user' as const,
          risk_levels: riskLevels,
          risk_action: riskAction,
          target_users: null,
          exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
          state: policyState,
        };

        if (mode === 'edit' && initialPolicy?.id) {
          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request);
        }
      } else if (policyType === 'app-protection') {
        if (mode === 'edit' && initialPolicy?.id) {
          // Edit mode: only update Conditional Access policy (MAM policy not editable via CA edit)
          const request = {
            policy_name: initialPolicy.displayName,
            policy_type: 'app-protection' as const,
            target_users: null,
            exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
            state: policyState,
          };

          await ConditionalAccessApiService.updateConditionalAccessPolicy(
            tenantId,
            initialPolicy.id,
            request
          );
        } else {
          // Create mode: create both MAM policy and CA policy
          const mamData = {
            display_name: mamDisplayName,
            description: mamDescription || undefined,
            pin_required: mamPinRequired,
            minimum_pin_length: mamMinimumPinLength,
          };

          if (mamPlatform === 'android') {
            await AppProtectionApiService.createAndroidPolicy(tenantId, mamData);
          } else {
            await AppProtectionApiService.createIosPolicy(tenantId, {
              ...mamData,
              face_id_blocked: mamFaceIdBlocked,
            });
          }

          // Create Conditional Access policy for App Protection
          await ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, {
            policy_name: mamDisplayName,
            policy_type: 'app-protection',
            target_users: null,
            exclude_users: excludedUsersList.length > 0 ? excludedUsersList : null,
            state: policyState,
          });
        }
      }

      onClose();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${mode === 'edit' ? 'update' : 'create'} policy`);
    } finally {
      setSaving(false);
    }
  };

  const getPolicyName = (): string => {
    if (policyType === 'session-controls') {
      return 'Session Controls';
    } else if (policyType === 'block-legacy-auth') {
      return 'Block Legacy Authentication';
    } else if (policyType === 'passwordless-enforcement') {
      return 'Passwordless Enforcement';
    } else if (policyType === 'risk-signin') {
      return 'Sign-In Risk Policy';
    } else if (policyType === 'risk-user') {
      return 'User Risk Policy';
    } else if (policyType === 'app-protection') {
      return 'App Protection';
    }
    return '';
  };

  const getPolicyAction = (): string => {
    if (policyType === 'session-controls') {
      return 'Control sessions';
    } else if (policyType === 'block-legacy-auth') {
      return 'BLOCK access';
    } else if (policyType === 'passwordless-enforcement') {
      return 'REQUIRE passwordless authentication';
    } else if (policyType === 'risk-signin') {
      return 'BLOCK high-risk sign-ins';
    } else if (policyType === 'risk-user') {
      if (riskAction === 'block') {
        return 'BLOCK access for high-risk users';
      } else if (riskAction === 'mfa') {
        return 'REQUIRE MFA for high-risk users';
      } else if (riskAction === 'passwordChange') {
        return 'REQUIRE password change for high-risk users';
      }
    }
    return '';
  };

  const getProtocols = (): string | null => {
    if (policyType === 'block-legacy-auth') {
      return 'Exchange ActiveSync, IMAP, POP3, SMTP, and other legacy protocols';
    }
    return null;
  };

  const getExcludedUsersCount = (): number => {
    return excludedUsers
      .split(',')
      .map((u) => u.trim())
      .filter((u) => u.length > 0).length;
  };

  const getPolicyStateLabel = (): string => {
    if (policyState === 'enabled') {
      return 'On';
    } else if (policyState === 'disabled') {
      return 'Off';
    } else if (policyState === 'enabledForReportingButNotEnforced') {
      return 'Report-only';
    }
    return '';
  };

  const getImpactPreview = (): string[] => {
    const impacts: string[] = [];

    // Common impacts
    if (policyType) {
      const excludedCount = getExcludedUsersCount();
      if (excludedCount > 0) {
        impacts.push(`${excludedCount} user(s) will be excluded from this policy`);
      }

      if (policyState === 'enabledForReportingButNotEnforced') {
        impacts.push('Policy will run in report-only mode (events logged but not enforced)');
      } else if (policyState === 'disabled') {
        impacts.push('Policy will be created but not active');
      }
    }

    // Policy-specific impacts
    if (policyType === 'session-controls') {
      if (signInFrequencyEnabled) {
        impacts.push(`Users will need to re-authenticate every ${signInFrequencyValue} ${signInFrequencyType}`);
      }
      if (persistentBrowserEnabled && persistentBrowserMode === 'never') {
        impacts.push('Users will NEVER stay signed in across browser sessions');
      } else if (persistentBrowserEnabled && persistentBrowserMode === 'always') {
        impacts.push('Users will stay signed in across browser sessions');
      }
      if (caeMode === 'strictEnforcement') {
        impacts.push('Access will be revoked in real-time for critical events (IP changes, token revocation)');
      } else if (caeMode === 'strictLocation') {
        impacts.push('Access will be revoked in real-time for location changes and critical events');
      }
    } else if (policyType === 'block-legacy-auth') {
      impacts.push('ALL legacy authentication attempts will be BLOCKED for all users in the tenant');
      impacts.push('Modern authentication clients (browser, mobile apps) will NOT be affected');
    } else if (policyType === 'passwordless-enforcement') {
      impacts.push('Users will be REQUIRED to use passwordless authentication (Microsoft Authenticator)');
      impacts.push('Traditional password authentication will be BLOCKED for all users');
      impacts.push('Users MUST have Microsoft Authenticator configured before this policy is enabled');
    } else if (policyType === 'risk-signin') {
      const riskLevelText = riskLevels.join(', ');
      impacts.push(`Sign-ins detected as ${riskLevelText} risk will be BLOCKED for all users`);
      impacts.push('Microsoft uses AI to detect anomalous sign-in attempts (impossible travel, anonymous IP, etc.)');
      impacts.push('Users will need to wait for risk to clear or contact administrator');
    } else if (policyType === 'risk-user') {
      const riskLevelText = riskLevels.join(', ');
      if (riskAction === 'block') {
        impacts.push(`Users detected as ${riskLevelText} risk will be BLOCKED from accessing resources`);
        impacts.push('Access will be restored once the user risk is remediated by an administrator');
      } else if (riskAction === 'mfa') {
        impacts.push(`Users detected as ${riskLevelText} risk will be REQUIRED to complete MFA`);
        impacts.push('MFA challenge will appear for ALL sign-ins while user risk is elevated');
      } else if (riskAction === 'passwordChange') {
        impacts.push(`Users detected as ${riskLevelText} risk will be REQUIRED to change their password AND complete MFA`);
        impacts.push('⚠️ WARNING: This action is incompatible with passwordless authentication');
      }
      impacts.push('Microsoft uses AI to detect compromised accounts (leaked credentials, suspicious behavior, etc.)');
    }

    return impacts;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            {mode === 'edit' ? 'Edit Conditional Access Policy' : 'Create Conditional Access Policy'}
          </DialogTitle>

          <DialogContent className={styles.scrollableContent}>
            {error && (
              <Alert intent="error" title="Error">
                {error}
              </Alert>
            )}

            <div className={styles.formContent}>
              {/* Policy Type Dropdown */}
              <Field label="Policy Type" required hint={mode === 'edit' ? 'Policy type cannot be changed when editing' : undefined}>
                {mode === 'edit' ? (
                  <Input value={getPolicyName()} disabled />
                ) : (
                  <Dropdown
                    className={styles.policyTypeDropdown}
                    placeholder="Select a policy type..."
                    value={policyType ? getPolicyName() : undefined}
                    selectedOptions={policyType ? [policyType] : []}
                    onOptionSelect={(_, data) => {
                      setPolicyType(data.optionValue as PolicyType);
                    }}
                  >
                    <Option value="app-protection">App Protection</Option>
                    <Option value="block-legacy-auth">Block Legacy Authentication</Option>
                    <Option value="passwordless-enforcement">Passwordless Enforcement</Option>
                    <Option value="session-controls">Session Controls</Option>
                    <Option value="risk-signin">Sign-In Risk Policy</Option>
                    <Option value="risk-user">User Risk Policy</Option>
                  </Dropdown>
                )}
              </Field>

              {/* Policy Summary Section */}
              {policyType && (
                <div className={styles.section}>
                  <Text size={400} weight="semibold">
                    Policy Summary
                  </Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Text size={300}>
                      <strong>Users affected:</strong> All users in the tenant
                    </Text>
                    <Text size={300}>
                      <strong>Apps affected:</strong> All cloud applications
                    </Text>
                    {getProtocols() && (
                      <Text size={300}>
                        <strong>Affected protocols:</strong> {getProtocols()}
                      </Text>
                    )}
                    <Text size={300}>
                      <strong>Action:</strong> {getPolicyAction()}
                    </Text>
                  </div>
                </div>
              )}

              {/* User Exclusions Section */}
              {policyType && (
                <div className={styles.section}>
                  <Text size={400} weight="semibold">
                    User Exclusions
                  </Text>
                  <Text size={300}>
                    Exclude specific users from this policy (e.g., break-glass admin accounts). Enter user principal names or Azure AD object IDs, comma-separated.
                  </Text>
                  <Field
                    label="Excluded Users (optional)"
                    hint="Example: admin@tenant.com, 00000000-0000-0000-0000-000000000001"
                  >
                    <Input
                      className={styles.excludedUsersInput}
                      value={excludedUsers}
                      onChange={(_, data) => setExcludedUsers(data.value)}
                      placeholder="user@domain.com, user-id, ..."
                    />
                  </Field>
                </div>
              )}

              {/* Policy State Section */}
              {policyType && (
                <div className={styles.section}>
                  <Text size={400} weight="semibold">
                    Policy State
                  </Text>
                  <Text size={300}>
                    Control whether the policy is active, inactive, or in report-only mode for testing.
                  </Text>
                  <Field label="Policy State" required>
                    <Dropdown
                      className={styles.policyStateDropdown}
                      value={getPolicyStateLabel()}
                      selectedOptions={[policyState]}
                      onOptionSelect={(_, data) => {
                        setPolicyState(data.optionValue as 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced');
                      }}
                    >
                      <Option value="enabledForReportingButNotEnforced">Report-only (recommended)</Option>
                      <Option value="enabled">On</Option>
                      <Option value="disabled">Off</Option>
                    </Dropdown>
                  </Field>
                  {policyState === 'enabledForReportingButNotEnforced' && (
                    <Text size={200} style={{ fontStyle: 'italic' }}>
                      Recommended: Start in report-only mode to test impact before enforcement
                    </Text>
                  )}
                </div>
              )}

              {/* Session Controls Form Content */}
              {policyType === 'session-controls' && (
                <>
                  {/* Sign-in Frequency Section */}
                  <div className={styles.section}>
                    <Text size={400} weight="semibold">
                      Sign-In Frequency
                    </Text>
                    <Text size={300}>
                      Control how often users must re-authenticate. When enabled, users will be required to sign in again after the specified time period.
                    </Text>

                    <Switch
                      label="Enable sign-in frequency"
                      checked={signInFrequencyEnabled}
                      onChange={(_, data) => setSignInFrequencyEnabled(data.checked)}
                    />

                    {signInFrequencyEnabled && (
                      <div className={styles.formRow}>
                        <Field
                          label="Frequency Value"
                          validationState={validationError ? 'error' : 'none'}
                          validationMessage={validationError || undefined}
                        >
                          <Input
                            type="number"
                            value={signInFrequencyValue.toString()}
                            onChange={(_, data) => {
                              const value = parseInt(data.value, 10);
                              if (!isNaN(value) && value > 0) {
                                setSignInFrequencyValue(value);
                              }
                            }}
                            min={1}
                            max={signInFrequencyType === 'hours' ? 24 : 365}
                          />
                        </Field>

                        <Field label="Frequency Unit">
                          <Dropdown
                            value={signInFrequencyType === 'hours' ? 'Hours' : 'Days'}
                            selectedOptions={[signInFrequencyType]}
                            onOptionSelect={(_, data) => {
                              const type = data.optionValue as 'hours' | 'days';
                              setSignInFrequencyType(type);
                            }}
                          >
                            <Option value="hours">Hours</Option>
                            <Option value="days">Days</Option>
                          </Dropdown>
                        </Field>
                      </div>
                    )}
                  </div>

                  {/* Persistent Browser Section */}
                  <div className={styles.section}>
                    <Text size={400} weight="semibold">
                      Persistent Browser Session
                    </Text>
                    <Text size={300}>
                      Control whether users stay signed in after closing their browser. This affects whether the &ldquo;Keep me signed in&rdquo; option is shown.
                    </Text>

                    <Switch
                      label="Enable persistent browser control"
                      checked={persistentBrowserEnabled}
                      onChange={(_, data) => setPersistentBrowserEnabled(data.checked)}
                    />

                    {persistentBrowserEnabled && (
                      <Field label="Persistent Browser Mode">
                        <Dropdown
                          className={styles.persistentBrowserDropdown}
                          value={persistentBrowserMode === 'always' ? 'Always' : 'Never'}
                          selectedOptions={[persistentBrowserMode]}
                          onOptionSelect={(_, data) => {
                            const mode = data.optionValue as 'always' | 'never';
                            setPersistentBrowserMode(mode);
                          }}
                        >
                          <Option value="always">Always</Option>
                          <Option value="never">Never</Option>
                        </Dropdown>
                      </Field>
                    )}
                  </div>

                  {/* Continuous Access Evaluation Section */}
                  <div className={styles.section}>
                    <Text size={400} weight="semibold">
                      Continuous Access Evaluation
                    </Text>
                    <Text size={300}>
                      Enable continuous access evaluation (CAE) to ensure real-time enforcement of critical events and policy changes.
                    </Text>

                    <Field label="CAE Mode">
                      <Dropdown
                        className={styles.caeDropdown}
                        value={
                          caeMode === 'strictEnforcement'
                            ? 'Strict Enforcement'
                            : caeMode === 'strictLocation'
                            ? 'Strict Location'
                            : 'Disabled'
                        }
                        selectedOptions={[caeMode]}
                        onOptionSelect={(_, data) => {
                          const mode = data.optionValue as 'strictEnforcement' | 'disabled' | 'strictLocation';
                          setCaeMode(mode);
                        }}
                      >
                        <Option value="strictEnforcement">Strict Enforcement</Option>
                        <Option value="strictLocation">Strict Location</Option>
                        <Option value="disabled">Disabled</Option>
                      </Dropdown>
                    </Field>
                  </div>
                </>
              )}

              {/* Block Legacy Auth Form Content */}
              {policyType === 'block-legacy-auth' && (
                <Text size={300}>
                  This policy will block legacy authentication protocols (IMAP, POP3, SMTP, Exchange ActiveSync) for all users in your tenant.
                </Text>
              )}

              {/* Passwordless Enforcement Form Content */}
              {policyType === 'passwordless-enforcement' && (
                <Text size={300}>
                  This policy will enforce passwordless authentication using Microsoft Authenticator for all users in your tenant. Users will be required to use the Microsoft Authenticator app with push notifications or passwordless phone sign-in. Traditional password authentication will be blocked.
                </Text>
              )}

              {/* Sign-In Risk Policy Form Content */}
              {policyType === 'risk-signin' && (
                <div className={styles.section}>
                  <Text size={400} weight="semibold">
                    Risk Levels to Block
                  </Text>
                  <Text size={300}>
                    Select which risk levels should trigger sign-in blocking. Microsoft&apos;s AI detects anomalous sign-in attempts based on factors like impossible travel, anonymous IP addresses, and unfamiliar locations.
                  </Text>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Checkbox
                      label="Low Risk"
                      checked={riskLevels.includes('low')}
                      onChange={(_, data) => {
                        if (data.checked) {
                          setRiskLevels([...riskLevels, 'low']);
                        } else {
                          setRiskLevels(riskLevels.filter(l => l !== 'low'));
                        }
                      }}
                    />
                    <Checkbox
                      label="Medium Risk"
                      checked={riskLevels.includes('medium')}
                      onChange={(_, data) => {
                        if (data.checked) {
                          setRiskLevels([...riskLevels, 'medium']);
                        } else {
                          setRiskLevels(riskLevels.filter(l => l !== 'medium'));
                        }
                      }}
                    />
                    <Checkbox
                      label="High Risk (Recommended)"
                      checked={riskLevels.includes('high')}
                      onChange={(_, data) => {
                        if (data.checked) {
                          setRiskLevels([...riskLevels, 'high']);
                        } else {
                          setRiskLevels(riskLevels.filter(l => l !== 'high'));
                        }
                      }}
                    />
                  </div>
                  {riskLevels.length === 0 && (
                    <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                      At least one risk level must be selected
                    </Text>
                  )}
                </div>
              )}

              {/* User Risk Policy Form Content */}
              {policyType === 'risk-user' && (
                <>
                  <div className={styles.section}>
                    <Text size={400} weight="semibold">
                      Risk Levels to Target
                    </Text>
                    <Text size={300}>
                      Select which risk levels should trigger the policy action. Microsoft&apos;s AI detects compromised accounts based on factors like leaked credentials, suspicious behavior, and malware infections.
                    </Text>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Checkbox
                        label="Low Risk"
                        checked={riskLevels.includes('low')}
                        onChange={(_, data) => {
                          if (data.checked) {
                            setRiskLevels([...riskLevels, 'low']);
                          } else {
                            setRiskLevels(riskLevels.filter(l => l !== 'low'));
                          }
                        }}
                      />
                      <Checkbox
                        label="Medium Risk"
                        checked={riskLevels.includes('medium')}
                        onChange={(_, data) => {
                          if (data.checked) {
                            setRiskLevels([...riskLevels, 'medium']);
                          } else {
                            setRiskLevels(riskLevels.filter(l => l !== 'medium'));
                          }
                        }}
                      />
                      <Checkbox
                        label="High Risk (Recommended)"
                        checked={riskLevels.includes('high')}
                        onChange={(_, data) => {
                          if (data.checked) {
                            setRiskLevels([...riskLevels, 'high']);
                          } else {
                            setRiskLevels(riskLevels.filter(l => l !== 'high'));
                          }
                        }}
                      />
                    </div>
                    {riskLevels.length === 0 && (
                      <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
                        At least one risk level must be selected
                      </Text>
                    )}
                  </div>

                  <div className={styles.section}>
                    <Text size={400} weight="semibold">
                      Risk Action
                    </Text>
                    <Text size={300}>
                      Choose what action to take when a user is detected as high-risk. Block is the most secure option.
                    </Text>

                    <Field label="Action">
                      <Dropdown
                        value={riskAction === 'block' ? 'Block Access' : riskAction === 'mfa' ? 'Require MFA' : 'Require Password Change + MFA'}
                        selectedOptions={[riskAction]}
                        onOptionSelect={(_, data) => {
                          setRiskAction(data.optionValue as 'block' | 'mfa' | 'passwordChange');
                        }}
                      >
                        <Option value="block">Block Access (Most Secure)</Option>
                        <Option value="mfa">Require MFA</Option>
                        <Option value="passwordChange">Require Password Change + MFA (Not compatible with passwordless)</Option>
                      </Dropdown>
                    </Field>

                    {riskAction === 'passwordChange' && (
                      <Text size={200} style={{ color: tokens.colorPaletteRedForeground1, fontStyle: 'italic' }}>
                        ⚠️ Warning: Password change actions are incompatible with passwordless authentication. Users with passwordless accounts will be unable to comply with this requirement.
                      </Text>
                    )}
                  </div>
                </>
              )}

              {/* App Protection (MAM) Configuration */}
              {policyType === 'app-protection' && (
                <>
                  {/* MAM Policy Section */}
                  <div className={styles.section}>
                    <Text size={500} weight="semibold">
                      App Protection Policy (MAM)
                    </Text>
                    <Text size={300} style={{ marginBottom: '16px' }}>
                      Creates a Mobile Application Management policy for protecting corporate data on unmanaged devices
                    </Text>

                    <div className={styles.section}>
                      <Text size={400} weight="semibold">
                        Platform
                      </Text>
                      <RadioGroup
                        value={mamPlatform}
                        onChange={(_, data) => setMamPlatform(data.value as 'android' | 'ios')}
                      >
                        <Radio value="android" label="Android" />
                        <Radio value="ios" label="iOS" />
                      </RadioGroup>
                    </div>

                    <div className={styles.section}>
                      <Text size={400} weight="semibold">
                        Policy Information
                      </Text>
                      <Field label="Display Name" required>
                        <Input
                          value={mamDisplayName}
                          onChange={(_, data) => setMamDisplayName(data.value)}
                          required
                          maxLength={256}
                          placeholder="e.g., Android Corporate Data Protection"
                        />
                      </Field>
                      <Field label="Description">
                        <Input
                          value={mamDescription}
                          onChange={(_, data) => setMamDescription(data.value)}
                          placeholder="Optional description of this policy"
                        />
                      </Field>
                    </div>

                    <div className={styles.section}>
                      <Text size={400} weight="semibold">
                        PIN Settings
                      </Text>
                      <Checkbox
                        label="PIN Required"
                        checked={mamPinRequired}
                        onChange={(_, data) => setMamPinRequired(data.checked as boolean)}
                      />
                      <Field label="Minimum PIN Length">
                        <Input
                          type="number"
                          value={String(mamMinimumPinLength)}
                          onChange={(e) => {
                            let value = Number(e.target.value);
                            if (value < 4) value = 4;
                            else if (value > 16) value = 16;
                            setMamMinimumPinLength(value);
                          }}
                          onBlur={(e) => {
                            let value = Number(e.target.value);
                            if (value < 4) value = 4;
                            else if (value > 16) value = 16;
                            if (value !== mamMinimumPinLength) {
                              setMamMinimumPinLength(value);
                            }
                          }}
                          min={4}
                          max={16}
                        />
                      </Field>
                    </div>

                    {mamPlatform === 'ios' && (
                      <div className={styles.section}>
                        <Text size={400} weight="semibold">
                          Biometric Settings
                        </Text>
                        <Checkbox
                          label="Face ID Blocked"
                          checked={mamFaceIdBlocked}
                          onChange={(_, data) => setMamFaceIdBlocked(data.checked as boolean)}
                        />
                      </div>
                    )}
                  </div>

                  <Divider style={{ margin: '24px 0' }} />

                  {/* Conditional Access Policy Section */}
                  <div className={styles.section}>
                    <Text size={500} weight="semibold">
                      Conditional Access Policy
                    </Text>
                    <Text size={300} style={{ marginBottom: '16px' }}>
                      Enforces the App Protection policy by requiring compliant or approved applications
                    </Text>

                    <Field label="Policy Name" hint="Uses the MAM policy display name">
                      <Input
                        value={mamDisplayName}
                        disabled
                        placeholder="Policy name will match MAM display name"
                      />
                    </Field>

                    <Field label="Policy State">
                      <Dropdown
                        value={policyState === 'enabled' ? 'On' : policyState === 'disabled' ? 'Off' : 'Report-only'}
                        selectedOptions={[policyState]}
                        onOptionSelect={(_, data) => setPolicyState(data.optionValue as 'enabled' | 'disabled' | 'enabledForReportingButNotEnforced')}
                      >
                        <Option value="enabled">On</Option>
                        <Option value="disabled">Off</Option>
                        <Option value="enabledForReportingButNotEnforced">Report-only</Option>
                      </Dropdown>
                    </Field>

                    <Field
                      label="Exclude Users (Optional)"
                      hint="Comma-separated list of user principal names or IDs to exclude from this policy"
                    >
                      <Input
                        value={excludedUsers}
                        onChange={(_, data) => setExcludedUsers(data.value)}
                        placeholder="e.g., user1@contoso.com, user2@contoso.com"
                      />
                    </Field>
                  </div>
                </>
              )}

              {/* Impact Preview Section */}
              {policyType && getImpactPreview().length > 0 && (
                <div className={styles.section}>
                  <Text size={400} weight="semibold">
                    Impact Preview
                  </Text>
                  <Text size={300}>
                    This policy will have the following effects when created:
                  </Text>
                  <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                    {getImpactPreview().map((impact, index) => (
                      <li key={index}>
                        <Text size={300}>{impact}</Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </DialogContent>

          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={handleClear} disabled={saving}>
              Clear
            </Button>
            <Button appearance="secondary" onClick={onClose} disabled={saving}>
              Close
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSave}
              disabled={
                !policyType ||
                saving ||
                !!validationError ||
                (policyType === 'app-protection' && mamDisplayName.trim().length === 0) ||
                ((policyType === 'risk-signin' || policyType === 'risk-user') && riskLevels.length === 0) ||
                (policyType === 'session-controls' && !signInFrequencyEnabled && !persistentBrowserEnabled)
              }
              loading={saving}
              loadingText="Saving..."
            >
              Save
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
